import { getAuth } from "firebase/auth";
import { UploadProduct } from "types/products";
import { updateUserConfig } from "models/configs";
import {
  waitForProductImport,
  uploadProductsToStorage,
} from "../../../utils/catalogUpload";
import { GlobalConfigType } from "types/configs";
import { AuthProp } from "types/auth";
import { ONBOARDING_STEPS } from "types/onboarding";
import { query, collection, where, onSnapshot } from "firebase/firestore";
import { db } from "firebaseClient";

export interface TenantCreationContext {
  tenantId: string;
  userEmail: string;
  displayName: string;
  companyName: string;
}

interface OnCallArgs {
  email: string;
  tenant: string;
  displayName: string;
  tenantDisplayName: string;
  skipStripeAccountCreation: boolean;
  isSelfServe: boolean;
}

export interface TenantCreationResult {
  tenantId: string;
  customToken: string;
}

const FIVE_MINUTES = 300000;
const TEN_SECONDS = 5000;

export class TenantCreationError extends Error {
  step: number;
  userMessage: string;
  technicalMessage: string;

  constructor(step: number, userMessage: string, technicalMessage: string) {
    super(userMessage);
    this.name = "TenantCreationError";
    this.step = step;
    this.userMessage = userMessage;
    this.technicalMessage = technicalMessage;
  }
}

export const createTenantResource = async (
  context: TenantCreationContext,
  onCall: (args: OnCallArgs) => Promise<TenantCreationResult>,
): Promise<TenantCreationResult | TenantCreationError> => {
  const args: OnCallArgs = {
    email: context.userEmail,
    tenant: context.tenantId,
    displayName: context.displayName,
    tenantDisplayName: context.companyName,
    skipStripeAccountCreation: true,
    isSelfServe: true,
  };

  const { tenantId, customToken } = await onCall(args);

  if (!tenantId) {
    return new TenantCreationError(
      1,
      "Failed to create tenant",
      "No tenant returned",
    );
  }

  if (!customToken) {
    return new TenantCreationError(
      2,
      "Failed to sign into new tenant",
      "No customToken returned",
    );
  }

  return { tenantId, customToken };
};

export const setupProductCatalog = async (
  tenantId: string,
  products: UploadProduct[],
): Promise<{ success: boolean; error?: string }> => {
  try {
    await uploadProductsToStorage(tenantId, products);
    await waitForProductImport(tenantId);
    return { success: true };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error during import";
    console.warn(`Product catalog setup failed: ${errorMessage}`);

    return {
      success: false,
      error: errorMessage,
    };
  }
};

export const finalizeOnboarding = async (
  auth: AuthProp,
  globalConfig: GlobalConfigType["globalConfig"],
  firebaseAuth: ReturnType<typeof getAuth>,
): Promise<void> => {
  const updatedConfig = {
    props: {
      onboarding: {
        currentStep: ONBOARDING_STEPS.complete,
      },
    },
  };

  await updateUserConfig(auth, globalConfig, updatedConfig);
  await firebaseAuth.currentUser?.getIdToken(true);
};

export const withOnboardingFlag = async <T>(
  operation: () => Promise<T>,
  firebaseAuth: ReturnType<typeof getAuth>,
): Promise<T> => {
  sessionStorage.setItem(ONBOARDING_STEPS.finalizing, "true");
  try {
    const result = await operation();

    sessionStorage.removeItem(ONBOARDING_STEPS.finalizing);

    await firebaseAuth.currentUser?.getIdToken(true);

    await new Promise((resolve) => setTimeout(resolve, 100));

    return result;
  } catch (error) {
    sessionStorage.removeItem(ONBOARDING_STEPS.finalizing);

    throw error;
  }
};

export const waitForCasesCreation = (
  tenantId: string,
): Promise<{ success: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const casesQuery = query(
      collection(db, "cases"),
      where("tenant", "==", tenantId),
    );

    const timeout = setTimeout(
      () => {
        unsub();
        console.warn("Case creation timed out - continuing anyway");

        resolve({
          success: false,
          error: "Case creation timed out, but your account is ready",
        });
      },
      process.env.REACT_APP_ENV === "local" ? TEN_SECONDS : FIVE_MINUTES,
    );

    const unsub = onSnapshot(
      casesQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          console.log(`${snapshot.size} cases created for tenant ${tenantId}`);
          clearTimeout(timeout);
          unsub();
          resolve({ success: true });
        }
      },
      (error) => {
        clearTimeout(timeout);
        unsub();
        console.warn("Error watching for cases:", error);

        resolve({
          success: false,
          error: "Could not verify case creation, but your account is ready",
        });
      },
    );
  });
};
